// Card do par (white label, método) — a folha das DUAS visões da tela.
//
// O que muda entre as visões é só o cabeçalho: na visão por método ele nomeia
// o white label (com o código copiável ao lado); na visão por white label
// nomeia o método, porque o white label já é o accordion que contém o card.
// O corpo — uma linha por configuração, com os combos de credencial e
// contingência e os DOIS switches (`active` e `default`) — é idêntico.
import { useTranslation } from "react-i18next"

import { CertificateStatusBadge } from "@/components/commons/certificate-status-badge"
import { CopyableValue } from "@/components/commons/copyable-value"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type { MerchantProcessor } from "@/types/admin-merchant-processor"
import type {
  ProcessorConfigItem,
  ProcessorGateway,
} from "@/types/admin-processor-config"

import {
  ACCENT_BY_CERTIFICATE_STATUS,
  isDefaultForMethod,
  minDaysToExpire,
  type PairGroup,
} from "../utils"
import { FallbackSelectCell } from "./fallback-select-cell"
import { ProcessorSelectCell } from "./processor-select-cell"

const panelPrefix = "pages.adminProcessorConfig"

type DefaultSwitchCellProps = {
  item: ProcessorConfigItem
  gatewayLabel: string
  methodLabel: string
  pending: boolean
  onElect: (item: ProcessorConfigItem) => void
}

// O toggle do padrão é ASSIMÉTRICO — só liga (a troca é encapsulada no
// backend): quem já é o padrão fica desabilitado com tooltip de transferência,
// e config inativa fica desabilitada com tooltip próprio (o backend recusaria
// com 409 de qualquer forma; a UI antecipa).
const DefaultSwitchCell = ({
  item,
  gatewayLabel,
  methodLabel,
  pending,
  onElect,
}: DefaultSwitchCellProps) => {
  const { t } = useTranslation()
  const isDefault = isDefaultForMethod(item)

  const disabledReason = isDefault
    ? t(
        `${panelPrefix}.byMethod.defaultAlreadyDefaultTooltip`
      )
    : !item.active
      ? t(
          `${panelPrefix}.byMethod.defaultDisabledInactiveTooltip`
        )
      : null

  const switchElement = (
    <Switch
      checked={isDefault}
      disabled={disabledReason !== null || pending}
      aria-label={t(
        `${panelPrefix}.byMethod.defaultToggleAria`,
        { method: methodLabel, gateway: gatewayLabel }
      )}
      onCheckedChange={(next) => {
        if (next) onElect(item)
      }}
    />
  )

  if (disabledReason === null) return switchElement

  // Switch desabilitado não emite eventos de ponteiro nem recebe foco — o
  // span focável é quem ancora o tooltip para mouse E teclado.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" tabIndex={0}>
            {switchElement}
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type PairConfigCardProps = {
  group: PairGroup
  /** Nome no cabeçalho: o white label ou o método, conforme a visão. */
  title: string
  /**
   * Identificador copiável ao lado do título. `null` quando o código já está
   * no accordion que contém o card — repetir só polui a varredura visual.
   */
  code?: string | null
  // Label amigável por gateway (metadata; fallback = enum).
  gatewayLabels: ReadonlyMap<ProcessorGateway, string>
  // Configs com mutação em voo (desabilita os switches da linha).
  pendingIds: ReadonlySet<string>
  methodLabelOf: (
    method: ProcessorConfigItem["method"]
  ) => string
  onToggleActive: (
    item: ProcessorConfigItem,
    nextActive: boolean
  ) => void
  // Pede a eleição do padrão — quem confirma é o painel, num alerta.
  onElectDefault: (item: ProcessorConfigItem) => void
  onChangeProcessor: (
    item: ProcessorConfigItem,
    processor: MerchantProcessor
  ) => void
  onChangeFallback: (
    item: ProcessorConfigItem,
    processor: MerchantProcessor | null
  ) => void
}

export const PairConfigCard = ({
  group,
  title,
  code = null,
  gatewayLabels,
  pendingIds,
  methodLabelOf,
  onToggleActive,
  onElectDefault,
  onChangeProcessor,
  onChangeFallback,
}: PairConfigCardProps) => {
  const { t } = useTranslation()

  return (
    <div
      data-testid={`pair-config-card-${group.whiteLabelId}-${group.method}`}
      className={cn(
        "overflow-hidden rounded-xl border border-l-4 bg-card shadow-sm transition-shadow hover:shadow-md",
        ACCENT_BY_CERTIFICATE_STATUS[
          group.certificateStatus
        ] ?? "border-l-zinc-300"
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/30 px-4 py-3">
        <div className="flex items-center gap-2 leading-tight">
          <p className="text-sm font-semibold">{title}</p>
          {/* O código do white label é identificador que ninguém digita:
              suporte cola no chamado, no Kibana e na consulta do banco. O
              rótulo acessível leva o NOME — dez botões de cópia com o mesmo
              rótulo deixam leitor de tela e comando de voz sem referência. */}
          {code && (
            <CopyableValue value={code} label={title} />
          )}
        </div>
        <div className="flex items-center gap-2">
          {group.hasActiveWithoutDefault && (
            <Badge
              variant="outline"
              data-testid={`no-default-badge-${group.whiteLabelId}-${group.method}`}
              className="rounded-full border-amber-200 bg-amber-50 font-medium text-amber-700"
            >
              {t(`${panelPrefix}.byMethod.noDefaultBadge`)}
            </Badge>
          )}
          <CertificateStatusBadge
            status={group.certificateStatus}
            daysToExpire={minDaysToExpire(group.items)}
          />
        </div>
      </div>
      <ul className="divide-y">
        {group.items.map((item) => {
          const gatewayLabel =
            gatewayLabels.get(item.gateway) ?? item.gateway
          const methodLabel = methodLabelOf(item.method)
          return (
            <li
              key={item.id}
              data-testid={`method-config-row-${item.id}`}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-muted/40"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">
                  {gatewayLabel}
                </span>
                <Badge
                  variant="outline"
                  className={cn(
                    "rounded-full border font-medium",
                    item.active
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-zinc-200 bg-zinc-50 text-zinc-600"
                  )}
                >
                  {t(
                    `${panelPrefix}.${item.active ? "active" : "inactive"}`
                  )}
                </Badge>
                {/* Badge textual além do switch: status nunca só por cor ou
                    controle — leitor de tela e varredura visual leem "Padrão". */}
                {isDefaultForMethod(item) && (
                  <Badge
                    variant="outline"
                    data-testid={`default-badge-${item.id}`}
                    className="rounded-full border-blue-200 bg-blue-50 font-medium text-blue-700"
                  >
                    {t(
                      `${panelPrefix}.byMethod.defaultBadge`
                    )}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-0.5">
                  <Label
                    htmlFor={`by-method-processor-${item.id}`}
                    className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {t(`${panelPrefix}.processorLabel`)}
                  </Label>
                  <ProcessorSelectCell
                    triggerId={`by-method-processor-${item.id}`}
                    item={item}
                    disabled={pendingIds.has(item.id)}
                    onSelect={onChangeProcessor}
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <Label
                    htmlFor={`by-method-fallback-${item.id}`}
                    className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {t(`${panelPrefix}.fallback.label`)}
                  </Label>
                  <FallbackSelectCell
                    triggerId={`by-method-fallback-${item.id}`}
                    item={item}
                    disabled={pendingIds.has(item.id)}
                    onSelect={onChangeFallback}
                  />
                </div>
                {/* Dois switches lado a lado precisam de rótulo visual — sem
                    ele, "qual dos dois desliga o meio de pagamento?" vira
                    adivinhação. Os spans são decorativos (aria-hidden): o nome
                    acessível de cada switch é o próprio aria-label. */}
                <div className="flex flex-col items-center gap-0.5">
                  <span
                    aria-hidden="true"
                    className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {t(`${panelPrefix}.active`)}
                  </span>
                  <Switch
                    checked={item.active}
                    disabled={pendingIds.has(item.id)}
                    aria-label={t(
                      `${panelPrefix}.toggleAria`,
                      {
                        method: methodLabel,
                        gateway: gatewayLabel,
                      }
                    )}
                    onCheckedChange={(nextActive) =>
                      onToggleActive(item, nextActive)
                    }
                  />
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <span
                    aria-hidden="true"
                    className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {t(
                      `${panelPrefix}.byMethod.defaultBadge`
                    )}
                  </span>
                  <DefaultSwitchCell
                    item={item}
                    gatewayLabel={gatewayLabel}
                    methodLabel={methodLabel}
                    pending={pendingIds.has(item.id)}
                    onElect={onElectDefault}
                  />
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
